import React, { FC, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import { KButton, KSpan } from "kahuna-base-react-components"

import { releasePlans, releases } from "../../../apis"
import { errorFilledToastOptions, successFilledToastOptions } from "../../../constants"
import { lang } from "../../../constants/languages"
import { CoManagerReleasePlan, CoManagerReleasePlanStep, CoManagerReleasePlanStepState } from "../../../types"
import { formatIsoDate, hashids, mapToCamelCase } from "../../../utility"
import Chevron from "./Chevron"
import ReleasePlanMetadata from "./ReleasePlanMetadata"
import "./CoManager.css"

export const COVER_TYPES = ["image/jpeg", "image/png"]
// The stores take a square cover no smaller than this. idol still has the last word on it, the check
// here only spares the artist a round trip for an image that was never going to pass.
const COVER_MIN_PX = 3000
const AUDIO_EXTENSIONS = [".wav", ".flac", ".aiff"]
// How often the plans are read again while a song converts, so the song step turns done or failed
// without the artist reloading the page.
const AUDIO_POLL_MS = 10000
const MARKETING_PATH = "/marketing"
const CATALOG_PATH = "/catalog"

export const STATE_COLORS: { [state in CoManagerReleasePlanStepState]: string } = {
  done: "#8FDF01",
  overdue: "#FF5C5C",
  due_soon: "#FF9558",
  pending: "#D6D6D6"
}

// Steps whose pages are not ready yet: they stay on the plan with their dates, said to be coming
// rather than opened onto a page that cannot do them.
export const COMING_SOON_STEPS = ["smart_link"]
// Steps the artist may skip: they stay on the plan, but count towards neither the progress nor the next step.
export const OPTIONAL_STEPS = ["discovery_mode"]
// How many plans the chat strip shows before the rest fold away behind the arrow.
const COLLAPSED_PLAN_COUNT = 2

const fill = (template: string, values: { [key: string]: string }) =>
  Object.entries(values).reduce((text, [key, value]) => text.split(`{${key}}`).join(value), template)

/**
 * Reads a picked cover as a data url, or says why it will not do: not a JPG or PNG, not square, or
 * under 3000 px a side.
 */
export const readCoverFile = (
  file: File,
  messages: { cover_invalid: string; cover_size: string }
): Promise<{ dataUrl?: string; error?: string }> =>
  new Promise((resolve) => {
    if (!COVER_TYPES.includes(file.type)) return resolve({ error: messages.cover_invalid })
    const reader = new FileReader()
    reader.onerror = () => resolve({ error: messages.cover_invalid })
    reader.onload = () => {
      const dataUrl = String(reader.result)
      const image = new Image()
      image.onerror = () => resolve({ error: messages.cover_invalid })
      image.onload = () => {
        const width = image.naturalWidth
        const height = image.naturalHeight
        if (width !== height || width < COVER_MIN_PX) {
          return resolve({ error: fill(messages.cover_size, { width: String(width), height: String(height) }) })
        }
        resolve({ dataUrl })
      }
      image.src = dataUrl
    }
    reader.readAsDataURL(file)
  })

/** Where a release's own page is: a draft is finished in the registry, anything sent is looked at on its page. */
export const releasePath = (releaseId: number | null, releaseStatus: string | null) => {
  if (!releaseId) return CATALOG_PATH
  const hash = hashids.encode(String(releaseId))
  return releaseStatus === "draft" ? `/catalog/asset-registry/${hash}` : `/catalog/${hash}`
}

/** Where a step is done, for a plan's step and an active release's alike. */
export const stepPath = (stepKey: string, releaseId: number | null, releaseStatus: string | null) => {
  if (stepKey === "smart_link" && releaseId) return `/catalog/smart-link/${hashids.encode(String(releaseId))}`
  // A draft opens straight on the pitching form, the last step of the release registry.
  if (stepKey === "pitching" && releaseId && releaseStatus === "draft") {
    return `/catalog/asset-registry/${hashids.encode(String(releaseId))}?step=pitching`
  }
  // And on its summary to be submitted, which also lists whatever is still missing before it can be.
  if (stepKey === "submit" && releaseId && releaseStatus === "draft") {
    return `/catalog/asset-registry/${hashids.encode(String(releaseId))}?step=summary`
  }
  if (stepKey === "press_release" || stepKey === "artist_photo") return MARKETING_PATH
  // Discovery Mode is opened from the catalog's filter bar, there is no page of its own to land on.
  if (stepKey === "discovery_mode") return CATALOG_PATH
  return releasePath(releaseId, releaseStatus)
}

/**
 * The artist's release plans, above the conversation. One row per plan with how far along it is and
 * the step due first, opened into the whole list of steps on demand.
 *
 * Nothing here ticks a step. Each step links to the page where it is done, and the server ticks it
 * off the data the next time the plans are read, so a step can never show done while the thing it
 * asks for is missing. The two exceptions are the cover and the song of a draft the plan opened,
 * which are uploaded from here because a fresh draft has nothing else on it to start from, and the
 * metadata, which is filled in here or in the release registry, whichever the artist picks.
 *
 * The chat shows it as a short strip above the conversation. The release plan page shows it in full
 * (variant "page"): every plan open and every step with a line on what it asks for.
 */
const ReleasePlanPanel: FC<{
  plans: CoManagerReleasePlan[]
  onChange: (plans: CoManagerReleasePlan[]) => void
  variant?: "chat" | "page"
}> = ({ plans, onChange, variant = "chat" }) => {
  const page = variant === "page"
  const navigate = useNavigate()
  const words = lang.co_manager
  const [openPlans, setOpenPlans] = useState<number[]>([])
  // The plans whose closed state the artist picked; on the page every other plan is open.
  const [closedPlans, setClosedPlans] = useState<number[]>([])
  const [metadataPlan, setMetadataPlan] = useState<number | null>(null)
  const [allPlansShown, setAllPlansShown] = useState(false)
  const [uploading, setUploading] = useState<string | null>(null)

  // The file pickers, one cover and one song input per plan, so a picked file always knows its plan.
  const inputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({})

  const replace = (updated: CoManagerReleasePlan) =>
    onChange(plans.map((plan) => (plan.id === updated.id ? updated : plan)))

  const failed = (failure: any) => {
    const messages = words.release_plan_errors as { [key: string]: string }
    const data = failure?.response?.data
    const detail = data?.detail || {}
    toast.error(
      fill(messages[data?.error] || messages.generic, {
        width: String(detail.width ?? ""),
        height: String(detail.height ?? "")
      }),
      errorFilledToastOptions
    )
  }

  const uploadCover = (planId: number, file: File | undefined) => {
    if (!file) return
    readCoverFile(file, words.release_plan_errors).then(({ dataUrl, error }) => {
      if (!dataUrl) {
        toast.error(error, errorFilledToastOptions)
        return
      }
      setUploading(`cover-${planId}`)
      releasePlans(`/${planId}/cover/`, "post", { cover: dataUrl })
        .then((response) => {
          replace(mapToCamelCase<CoManagerReleasePlan>(response.data))
          toast.success(words.release_plan_cover_uploaded, successFilledToastOptions)
        })
        .catch(failed)
        .finally(() => setUploading(null))
    })
  }

  // The song goes up the way it does from the release registry: the plan opens the track it lands on
  // (title, artists, ISRC) and hands back the registry's asset title, then the file is sent to the
  // registry's own upload call with them. True once the file is handed over.
  const uploadOne = (planId: number, file: File): Promise<boolean> =>
    releasePlans(`/${planId}/track/`, "post", { file_name: file.name, file_size: file.size }).then((response) => {
      const prepared = mapToCamelCase<{
        trackId: string
        productId: string
        assetTitle: string
        plan: CoManagerReleasePlan
      }>(response.data)
      const formData = new FormData()
      formData.append("file", file)
      return releases(
        `/release/label_camp_upload_track_audio/?product_id=${prepared.productId}&track_id=${
          prepared.trackId
        }&asset_title=${encodeURIComponent(prepared.assetTitle)}`,
        "post",
        formData
      ).then((uploaded) => {
        if (!uploaded?.data?.status) {
          toast.error(uploaded?.data?.message || words.release_plan_errors.audio_not_uploaded, errorFilledToastOptions)
          return false
        }
        return true
      })
    })

  // The songs of an EP or an album are picked together, as many as are still missing, and go up one
  // after the other: each opens its own track with the next number, so they cannot race for one.
  const uploadAudio = async (plan: CoManagerReleasePlan, files: File[]) => {
    const remaining = Math.max((plan.trackCount || 1) - (plan.uploadedTrackCount || 0), 0)
    const picked = files.slice(0, remaining)
    if (!picked.length) return
    setUploading(`audio-${plan.id}`)
    let started = 0
    try {
      for (const file of picked) {
        if (await uploadOne(plan.id, file)) started += 1
      }
    } catch (failure) {
      failed(failure)
    }
    if (started) toast.success(words.release_plan_audio_started, successFilledToastOptions)
    setUploading(null)
    // Read again so the step shows the songs converting, which also starts the polling below.
    reload()
  }

  // Read again after the metadata form saved, which is what ticks the metadata step off.
  const reload = () =>
    releasePlans("/", "get")
      .then((response) => onChange(mapToCamelCase<{ results: CoManagerReleasePlan[] }>(response.data).results))
      .catch(() => undefined)

  const converting = plans.some((plan) => plan.audioUpload === "in_progress")
  useEffect(() => {
    if (!converting) return
    const timer = setInterval(reload, AUDIO_POLL_MS)
    return () => clearInterval(timer)
  }, [converting])

  const stepWords = words.release_plan_steps as { [key: string]: string }
  const stepHints = words.release_plan_step_hints as { [key: string]: string }
  const stateText = (state: CoManagerReleasePlanStepState) =>
    ({
      done: words.release_plan_state_done,
      overdue: words.release_plan_state_overdue,
      due_soon: words.release_plan_state_due_soon,
      pending: ""
    }[state])

  const linkButton = (text: string, onClick: () => void, disabled = false, dataTestId?: string) => (
    <KButton
      dataTestId={dataTestId}
      text={text}
      onClick={onClick}
      disabled={disabled}
      background="transparent"
      shadowDisabled
      width="auto"
      height="auto"
      padding="0px"
      fontSize={12}
      textColor="#6B7280"
    />
  )

  const stepAction = (plan: CoManagerReleasePlan, step: CoManagerReleasePlanStep) => {
    // The metadata form stays open once its save ticks the step, so it keeps the button that closes it.
    if (step.key === "metadata" && metadataPlan === plan.id && step.completed) {
      return linkButton(words.release_plan_metadata_close, () => setMetadataPlan(null))
    }
    if (step.completed) return null
    const draft = plan.releaseStatus === "draft"
    if (draft && step.key === "cover") {
      const busy = uploading === `cover-${plan.id}`
      return linkButton(
        busy ? words.release_plan_uploading : words.release_plan_upload_cover,
        () => inputRefs.current[`cover-${plan.id}`]?.click(),
        !!uploading,
        "co-manager-plan-upload-cover"
      )
    }
    if (draft && step.key === "audio") {
      const several = (plan.trackCount || 1) > 1
      const allPicked = (plan.uploadedTrackCount || 0) >= (plan.trackCount || 1)
      // A single converting at idol, or every song of the release up: another upload now would only put
      // a second copy of a song on the draft.
      if (plan.audioUpload === "in_progress" && (!several || allPicked)) {
        return <KSpan text={words.release_plan_audio_processing} fontSize={12} lineHeight="16px" color="#999" />
      }
      const busy = uploading === `audio-${plan.id}`
      return linkButton(
        busy
          ? words.release_plan_uploading
          : several
          ? words.release_plan_upload_audios
          : words.release_plan_upload_audio,
        () => inputRefs.current[`audio-${plan.id}`]?.click(),
        !!uploading,
        "co-manager-plan-upload-audio"
      )
    }
    if (draft && step.key === "metadata") {
      const open = metadataPlan === plan.id
      return (
        <div className="co-manager-plan-step-actions">
          {linkButton(
            open ? words.release_plan_metadata_close : words.release_plan_metadata_fill,
            () => setMetadataPlan(open ? null : plan.id),
            false,
            "co-manager-plan-metadata-fill"
          )}
          {linkButton(words.release_plan_metadata_wizard_short, () =>
            navigate(releasePath(plan.releaseId, plan.releaseStatus))
          )}
        </div>
      )
    }
    if (COMING_SOON_STEPS.includes(step.key)) {
      return <KSpan text={words.release_plan_coming_soon} fontSize={12} lineHeight="16px" color="#999" />
    }
    return linkButton(words.release_plan_open, () => navigate(stepPath(step.key, plan.releaseId, plan.releaseStatus)))
  }

  const renderStep = (plan: CoManagerReleasePlan, step: CoManagerReleasePlanStep) => (
    <React.Fragment key={`plan-${plan.id}-step-${step.id}`}>
      <div className="co-manager-plan-step">
        <div className="co-manager-status-dot" style={{ backgroundColor: STATE_COLORS[step.state] }} />
        <div className="co-manager-plan-step-text">
          <KSpan
            text={stepWords[step.key] || step.key}
            fontSize={13}
            lineHeight="18px"
            color={step.completed ? "#999" : "#111"}
          />
          <KSpan
            text={[
              fill(words.release_plan_due, { date: formatIsoDate(step.dueDate) }),
              stateText(step.state),
              step.key === "audio" && !step.completed && (plan.trackCount || 1) > 1
                ? fill(words.release_plan_audio_progress, {
                    done: String(plan.uploadedTrackCount || 0),
                    total: String(plan.trackCount)
                  })
                : "",
              step.key === "audio" && !step.completed && plan.audioUpload === "failed"
                ? words.release_plan_audio_failed
                : ""
            ]
              .filter(Boolean)
              .join(" · ")}
            fontSize={11}
            lineHeight="15px"
            color={step.state === "overdue" ? "#FF5C5C" : "#999"}
          />
          {page && !step.completed && !COMING_SOON_STEPS.includes(step.key) && !!stepHints[step.key] && (
            <KSpan text={stepHints[step.key]} fontSize={11} lineHeight="15px" color="#999" />
          )}
          {/* The drawer has no room for the other hints, but a cover picked blind is one refused later. */}
          {!page && !step.completed && step.key === "cover" && plan.releaseStatus === "draft" && (
            <KSpan text={words.release_plan_cover_rules} fontSize={11} lineHeight="15px" color="#999" />
          )}
        </div>
        {stepAction(plan, step)}
      </div>
      {step.key === "metadata" && metadataPlan === plan.id && <ReleasePlanMetadata plan={plan} onSaved={reload} />}
    </React.Fragment>
  )

  const renderPlan = (plan: CoManagerReleasePlan) => {
    const open = page ? !closedPlans.includes(plan.id) : openPlans.includes(plan.id)
    const toggle = () =>
      page
        ? setClosedPlans((previous) => (open ? [...previous, plan.id] : previous.filter((id) => id !== plan.id)))
        : setOpenPlans((previous) => (open ? previous.filter((id) => id !== plan.id) : [...previous, plan.id]))
    const required = plan.steps.filter((step) => !OPTIONAL_STEPS.includes(step.key))
    const next = required.find((step) => !step.completed)

    return (
      <div className="co-manager-plan" key={`plan-${plan.id}`}>
        <div className="co-manager-plan-head">
          <div className="co-manager-plan-head-text">
            <KSpan text={plan.title} fontSize={14} lineHeight="20px" fontWeight={500} color="#111" ellipsis />
            <KSpan
              text={[
                fill(words.release_plan_release_on, { date: formatIsoDate(plan.releaseDate) }),
                fill(words.release_plan_progress, {
                  done: String(required.filter((step) => step.completed).length),
                  total: String(required.length)
                })
              ].join(" · ")}
              fontSize={12}
              lineHeight="16px"
              color="#999"
            />
          </div>
          <div className="co-manager-plan-next">
            {next ? (
              <>
                <div className="co-manager-status-dot" style={{ backgroundColor: STATE_COLORS[next.state] }} />
                <KSpan
                  text={fill(words.release_plan_next, { step: stepWords[next.key] || next.key })}
                  fontSize={12}
                  lineHeight="16px"
                  color="#111"
                  ellipsis
                />
              </>
            ) : (
              <KSpan text={words.release_plan_all_done} fontSize={12} lineHeight="16px" color="#111" />
            )}
          </div>
          {/* The plan itself is not edited: its release is, in the registry, and the plan follows it. */}
          {!!plan.releaseId &&
            linkButton(
              plan.releaseStatus === "draft" ? words.release_plan_edit_release : words.release_plan_view_release,
              () => navigate(releasePath(plan.releaseId, plan.releaseStatus)),
              false,
              "co-manager-plan-edit-release"
            )}
          {linkButton(
            open ? words.release_plan_hide : words.release_plan_show,
            toggle,
            false,
            "co-manager-plan-toggle"
          )}
        </div>
        {open && (
          <div className="co-manager-plan-steps">
            {plan.steps.map((step) => renderStep(plan, step))}
            {/* Removing a plan goes through the Kahuna team, so plans are not opened and dropped at will. */}
            <div className="co-manager-plan-footer">
              <KSpan text={words.release_plan_remove_contact} fontSize={12} lineHeight="16px" color="#999" />
            </div>
          </div>
        )}
        {/* The pickers the step buttons open: KButton clicks them, so nothing here is a bare control. */}
        <input
          ref={(element) => (inputRefs.current[`cover-${plan.id}`] = element)}
          data-testid={`co-manager-plan-cover-input-${plan.id}`}
          type="file"
          accept={COVER_TYPES.join(",")}
          className="hidden"
          onChange={(event) => {
            uploadCover(plan.id, event.target.files?.[0])
            event.target.value = ""
          }}
        />
        <input
          ref={(element) => (inputRefs.current[`audio-${plan.id}`] = element)}
          data-testid={`co-manager-plan-audio-input-${plan.id}`}
          type="file"
          accept={AUDIO_EXTENSIONS.join(",")}
          multiple={(plan.trackCount || 1) > 1}
          className="hidden"
          onChange={(event) => {
            uploadAudio(plan, Array.from(event.target.files || []))
            event.target.value = ""
          }}
        />
      </div>
    )
  }

  if (!plans.length) return null

  // The chat strip folds away all but the first plans; the page always lists every one.
  const foldable = !page && plans.length > COLLAPSED_PLAN_COUNT
  const shownPlans = foldable && !allPlansShown ? plans.slice(0, COLLAPSED_PLAN_COUNT) : plans

  return (
    <div className={page ? "co-manager-plans co-manager-plans-page" : "co-manager-plans"}>
      {!page && (
        <div className="co-manager-plans-head">
          <KSpan
            text={words.release_plans_title.toLocaleUpperCase()}
            fontSize={11}
            lineHeight="14px"
            fontWeight={500}
            letterSpacing="0.66px"
            color="#999"
          />
          {foldable && (
            <button
              type="button"
              className="co-manager-plans-fold"
              data-testid="co-manager-plans-fold"
              onClick={() => setAllPlansShown(!allPlansShown)}
            >
              {!allPlansShown && (
                <KSpan text={`+${plans.length - COLLAPSED_PLAN_COUNT}`} fontSize={11} lineHeight="14px" color="#999" />
              )}
              <Chevron direction={allPlansShown ? "up" : "down"} size={18} />
            </button>
          )}
        </div>
      )}
      {shownPlans.map(renderPlan)}
    </div>
  )
}

export default ReleasePlanPanel
